import { useState } from 'react';

import {
  faAngleDown,
  faBars,
  faChartGantt,
  faGripVertical,
  faLineColumns,
  faMapLocation
} from '@fortawesome/pro-light-svg-icons';
import { useNavParams, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { View, ViewContext } from '../../enums';

import { GRAY_LIGHT_SECONDARY } from 'constants/colors';

import { getViewContext } from '../../utils';

import { selectedGroupingScheduleAtom } from '../../atoms';

import { Button, Menu, MenuItem } from '@mui/material';

import { FontAwesomeSvgIcon } from 'components/shared';

type Props = {
  view: View;
  handleChangeView: (view: View) => void;
  disabled?: boolean;
};

const viewIcon = {
  [View.Roster]: faGripVertical,
  [View.Timeline]: faChartGantt,
  [View.List]: faBars,
  [View.Cards]: faLineColumns,
  [View.Map]: faMapLocation
};

export default function ViewSwitch({
  view,
  handleChangeView,
  disabled
}: Props) {
  const { t } = useTranslation('common');
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasKidsPlanAccess
  } = useSelector(({ grants }) => grants);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [, setSelectedGroupingSchedule] = useRecoilState(
    selectedGroupingScheduleAtom
  );
  const { params, setParams } = useNavParams();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  let views: View[] = [];
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess ||
    hasKidsPlanAccess
  ) {
    views.push(View.Roster);
  }
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess
  ) {
    views.push(View.List);
  }
  if (hasActivityPlanningViewAccess) {
    views.push(View.Timeline);
    views.push(View.Cards);
    views.push(View.Map);
  }

  // Only Roster and List view when viewing My Activities
  if (getViewContext() === ViewContext.Own) {
    views = views.filter(
      (view: View) => view === View.List || view === View.Roster
    );
  }

  if (!views.includes(view)) {
    setParams({ ...params, view: views[0] });
    handleChangeView(views[0]);
  }

  return (
    <div>
      <Button
        variant="contained"
        color="lightGray"
        sx={{
          px: '14px',
          height: '40px',
          border: `1px solid ${GRAY_LIGHT_SECONDARY}`
        }}
        onClick={handleClick}
        startIcon={<FontAwesomeSvgIcon icon={viewIcon[view]} sx={{ mr: 1 }} />}
        endIcon={<FontAwesomeSvgIcon icon={faAngleDown} sx={{ ml: 1 }} />}
        disabled={disabled}
      >
        {t(`activityPlanning.view.${view}`)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {views.map((v) => (
          <MenuItem
            key={v}
            color="neutral"
            onClick={() => {
              setParams({ ...params, view: v });
              handleChangeView(v);
              if (v !== View.Map) {
                setSelectedGroupingSchedule('');
              }
              setAnchorEl(null);
            }}
          >
            <FontAwesomeSvgIcon
              icon={viewIcon[v]}
              sx={{ mr: 1.5 }}
              fontSize="small"
            />
            {t(`activityPlanning.view.${v}`)}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
